import { type ComponentProps, type ReactNode } from 'react'
import { Combobox as BaseCombobox } from '@base-ui/react/combobox'
import { twMerge } from 'tailwind-merge'
import { useFieldRequired } from '../field/nested/context'
import { ComboboxInput } from './nested/input'
import { ComboboxTrigger } from './nested/trigger'
import { ComboboxList } from './nested/list'
import { ComboboxItem } from './nested/item'
import { ComboboxChip } from './nested/chip'
import { ComboboxChipRemove } from './nested/chip-remove'
import { ComboboxEmpty } from './nested/empty'
import { ComboboxClear } from './nested/clear'
import { ComboboxPortal } from './nested/portal'
import { ComboboxPositioner } from './nested/positioner'
import { ComboboxPopup } from './nested/popup'
import { ComboboxBackdrop } from './nested/backdrop'
import { ComboboxValue } from './nested/value'
import { ComboboxContent } from './nested/content'
import { ComboboxChips } from './nested/chips'
import { ComboboxGroupLabel } from './nested/group-label'
import { ListItemText } from '../../lib/list-item-text'
import type { PartProps } from '../../lib/part-props'

interface ComboboxProps extends PartProps<ComponentProps<typeof BaseCombobox.Root>> {
  className?: string
  children: ReactNode
}

function Combobox({ className, children, required, ...props }: ComboboxProps) {
  return (
    <BaseCombobox.Root required={useFieldRequired(required)} {...props}>
      <div
        data-slot="combobox"
        className={twMerge(
          'text-foreground-secondary relative flex w-64 flex-col gap-1 text-sm font-medium',
          className,
        )}
      >
        {children}
      </div>
    </BaseCombobox.Root>
  )
}

Combobox.Input = ComboboxInput
Combobox.Trigger = ComboboxTrigger
Combobox.List = ComboboxList
Combobox.Item = ComboboxItem
Combobox.Chip = ComboboxChip
Combobox.ChipRemove = ComboboxChipRemove
Combobox.Empty = ComboboxEmpty
Combobox.Clear = ComboboxClear
Combobox.Portal = ComboboxPortal
Combobox.Positioner = ComboboxPositioner
Combobox.Popup = ComboboxPopup
Combobox.Backdrop = ComboboxBackdrop
Combobox.Value = ComboboxValue
Combobox.Content = ComboboxContent
Combobox.Chips = ComboboxChips
Combobox.ItemIndicator = BaseCombobox.ItemIndicator
Combobox.Icon = BaseCombobox.Icon
Combobox.Group = BaseCombobox.Group
Combobox.GroupLabel = ComboboxGroupLabel
Combobox.ItemText = ListItemText
Combobox.Collection = BaseCombobox.Collection

export { Combobox }
